import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, ChevronDown } from "lucide-react";
import { useAppState } from "../stores";
import { useActiveSessionId } from "../stores/chat-selectors";
import { sessionConnectorsQueryOptions } from "../queries/connectors-queries";
import { useChatActions } from "./ChatActionsContext";

/**
 * Renders one card per pending Strands HITL interrupt published by the
 * backend `ApprovalHook` (see `backend/stream.py::ApprovalHook`). State is
 * driven entirely by `state.pending_approvals` — there are no
 * tool_call events for the paused tool because the hook fires before the
 * `ToolHookBroadcaster`, so the only signal the UI gets is the state
 * snapshot/delta.
 *
 * Titles come from `/api/sessions/{id}/connectors`, which lists each
 * connector's tools with the human-readable `title` field from the MCP
 * `Tool.title` (spec 2025-06+). The prefixed name in
 * `state.pending_approvals[id].tool` keys into that map directly.
 */
interface PendingApproval {
  name?: string;
  tool?: string;
  args?: Record<string, unknown>;
  toolUseId?: string;
}

export function ApprovalBanner() {
  const state = useAppState();
  const sessionId = useActiveSessionId();
  const { respondToInterrupt } = useChatActions();

  const pending = (state.pending_approvals as Record<string, PendingApproval> | undefined) || {};
  const ids = Object.keys(pending);

  // Only fetch connectors when there's actually a pending approval to
  // resolve a title for — the popover endpoint is otherwise idle.
  const { data: connectorsData } = useQuery({
    ...sessionConnectorsQueryOptions(sessionId),
    enabled: !!sessionId && ids.length > 0,
  });

  const titleMap = useMemo(() => {
    const out: Record<string, { title: string | null; description: string }> = {};
    for (const c of connectorsData?.connectors ?? []) {
      for (const t of c.tools ?? []) {
        out[t.name] = { title: t.title ?? null, description: t.description ?? "" };
      }
    }
    return out;
  }, [connectorsData]);

  if (ids.length === 0) return null;

  // Fire-and-forget — the optimistic clear inside respondToInterrupt
  // unmounts this card immediately; the run completes in the background.
  const handle = (id: string, response: "A" | "D") => {
    void respondToInterrupt(id, response);
  };

  return (
    <div className="approval-stack">
      {ids.map((id) => (
        <ApprovalCard
          key={id}
          id={id}
          pending={pending[id] || {}}
          titleMap={titleMap}
          onRespond={handle}
        />
      ))}
    </div>
  );
}

function ApprovalCard({
  id,
  pending,
  titleMap,
  onRespond,
}: {
  id: string;
  pending: PendingApproval;
  titleMap: Record<string, { title: string | null; description: string }>;
  onRespond: (id: string, response: "A" | "D") => void;
}) {
  const [open, setOpen] = useState(false);
  const args = pending.args || {};
  const argsJson = JSON.stringify(args, null, 2);
  const hasArgs = argsJson && argsJson !== "{}";
  const meta = pending.tool ? titleMap[pending.tool] : undefined;
  const heading = meta?.title || pending.tool || "Инструмент";

  return (
    <div className="approval-card">
      <div className="approval-card-head">
        <CircleAlert className="approval-card-icon" size={16} aria-hidden />
        <span className="approval-card-label">Вызов инструмента</span>
        <span className="approval-card-sep" aria-hidden>·</span>
        <span className="approval-card-name">{heading}</span>
        {hasArgs && (
          <button
            type="button"
            className="approval-card-toggle"
            data-open={open}
            aria-expanded={open}
            aria-label="Параметры вызова"
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronDown size={14} />
          </button>
        )}
        <div className="approval-card-actions">
          <button
            type="button"
            className="approval-btn approval-btn-deny"
            onClick={() => onRespond(id, "D")}
          >
            Отклонить
          </button>
          <button
            type="button"
            className="approval-btn approval-btn-approve"
            onClick={() => onRespond(id, "A")}
          >
            Разрешить
          </button>
        </div>
      </div>

      {hasArgs && open && <pre className="approval-card-args">{argsJson}</pre>}
    </div>
  );
}
